import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "../root-route";
import { BobinesPage } from "./bobines";
import { CadresPage } from "./cadres";
import { ColoriageAccueilPage } from "./coloriage-accueil";
import { ColoriageAveuglePage } from "./coloriage-aveugle";
import { ColoriageCahierPage } from "./coloriage-cahier";
import { ColoriageComparaisonPage } from "./coloriage-comparaison";
import { ColoriagePagePage } from "./coloriage-page";
import { ColoriageVariantesPage } from "./coloriage-variantes";
import { HistoiresAccueilPage } from "./histoires-accueil";
import { HistoiresDoublePage } from "./histoires-double";
import { HistoiresLivrePage } from "./histoires-livre";
import { LivresJeuxAccueilPage } from "./livres-jeux-accueil";
import { LivresJeuxLivrePage, type OngletJeu } from "./livres-jeux-livre";
import { LivresJeuxScenePage } from "./livres-jeux-scene";
import { GuideHorsIdeePage, GuideIdeePage, RelancePage } from "./portefeuille-guide";
import { IdeePage } from "./portefeuille-idee";
import { PortefeuillePage } from "./portefeuille-liste";
import { ScoutPage } from "./portefeuille-scout";
import { SilhouettesPage } from "./silhouettes-page";

/**
 * Routes de l'app. Fichier possede par l'app : `src/router.tsx` (template) les ajoute a
 * l'arbre a cote de celles de la couche Lab, et ne reecrit pas ce fichier.
 *
 * Vingt et une adresses : les trois de la spec du portefeuille (section 3), les trois des
 * gestes guides (spec du 17 septembre 2026, section 4), `/silhouettes` et `/cadres`, les
 * modules sans etat des issues #145 et #167, `/bobines` (#220), les six du module coloriage
 * (spec du 21 septembre 2026, section 3), l'accueil, un livre et une double du module
 * Histoires (spec du 26 septembre 2026, section 3), puis l'accueil, un livre et une scene des
 * livres-jeux (spec du 3 octobre 2026, #276). Le prefixe `/portefeuille/` laisse la place aux
 * modules suivants, comme `/coloriage/...` puis `/histoires/...` l'ont prise sans rien
 * deplacer ici.
 *
 * `relance` est un segment fixe sous `$slug`, et `gestes/$nom` un chemin plus long que
 * `$slug` : le routeur prend toujours la route la plus precise, une idee qui s'appellerait
 * `relance` ne masquerait donc rien.
 */

export const portefeuilleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: PortefeuillePage,
});

export const ideeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portefeuille/idees/$slug",
  component: function IdeeRouteComponent() {
    const { slug } = ideeRoute.useParams();
    return <IdeePage slug={slug} />;
  },
});

export const gesteIdeeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portefeuille/idees/$slug/gestes/$nom",
  component: function GesteIdeeRouteComponent() {
    const { slug, nom } = gesteIdeeRoute.useParams();
    return <GuideIdeePage nom={nom} slug={slug} />;
  },
});

export const relanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portefeuille/idees/$slug/relance",
  component: function RelanceRouteComponent() {
    const { slug } = relanceRoute.useParams();
    return <RelancePage slug={slug} />;
  },
});

export const gesteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portefeuille/gestes/$nom",
  component: function GesteRouteComponent() {
    const { nom } = gesteRoute.useParams();
    return <GuideHorsIdeePage nom={nom} />;
  },
});

export const scoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portefeuille/scout/$id",
  component: function ScoutRouteComponent() {
    const { id } = scoutRoute.useParams();
    return <ScoutPage id={id} />;
  },
});

/**
 * Le module Silhouettes (#145) et son mode serre-livres (#337). Le mode passe dans l'adresse
 * (`?mode=serre-livres`) : un rechargement ou un lien copie y retombent. Une autre valeur est
 * effacee (`undefined`, pas seulement omise : le routeur fusionne ce que rend `validateSearch`
 * avec l'adresse lue), la page d'une silhouette s'affiche.
 */
export const silhouettesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/silhouettes",
  validateSearch: (search: Record<string, unknown>): { mode?: "serre-livres" | undefined } => ({
    mode: search.mode === "serre-livres" ? "serre-livres" : undefined,
  }),
  component: function SilhouettesRouteComponent() {
    const { mode } = silhouettesRoute.useSearch();
    return <SilhouettesPage mode={mode ?? "silhouette"} />;
  },
});

export const cadresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cadres",
  component: CadresPage,
});

/** Le module Bobines (#220) : le stock de filament et la liste de courses, une seule page. */
export const bobinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bobines",
  component: function BobinesRouteComponent() {
    return <BobinesPage />;
  },
});

/**
 * Le module coloriage (#151) : l'accueil, un cahier, une page a juger. Le numero de la
 * version affichee passe dans l'adresse (`?v=2`) : un rechargement, un lien copie ou le
 * retour arriere retombent sur la meme version. Une valeur qui n'est pas un entier positif
 * est ignoree, la page choisit alors elle-meme.
 */
export const coloriageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coloriage",
  component: ColoriageAccueilPage,
});

export const cahierColoriageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coloriage/cahiers/$id",
  component: function CahierColoriageRouteComponent() {
    const { id } = cahierColoriageRoute.useParams();
    return <ColoriageCahierPage id={id} />;
  },
});

export const pageColoriageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coloriage/pages/$id",
  validateSearch: (search: Record<string, unknown>): { v?: number } => {
    const v = Number(search.v);
    return Number.isInteger(v) && v > 0 ? { v } : {};
  },
  component: function PageColoriageRouteComponent() {
    const { id } = pageColoriageRoute.useParams();
    const { v } = pageColoriageRoute.useSearch();
    return <ColoriagePagePage id={id} v={v} />;
  },
});

/**
 * Les variantes du prompt et les comparaisons a l'aveugle (#153). `?depuis=<comparaison>` sur
 * `/coloriage/variantes` pre-remplit le bloc de lancement avec le cahier, le nombre de versions
 * et les variantes de cette comparaison (« Relancer sur d'autres pages ») ; une valeur qui n'est
 * pas un entier positif est ignoree. `juger` est un segment fixe sous `comparaisons/$id` : le
 * routeur prend la route la plus precise.
 */
export const variantesColoriageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coloriage/variantes",
  validateSearch: (search: Record<string, unknown>): { depuis?: number } => {
    const depuis = Number(search.depuis);
    return Number.isInteger(depuis) && depuis > 0 ? { depuis } : {};
  },
  component: function VariantesColoriageRouteComponent() {
    const { depuis } = variantesColoriageRoute.useSearch();
    return <ColoriageVariantesPage depuis={depuis} />;
  },
});

export const comparaisonColoriageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coloriage/comparaisons/$id",
  component: function ComparaisonColoriageRouteComponent() {
    const { id } = comparaisonColoriageRoute.useParams();
    return <ColoriageComparaisonPage id={id} />;
  },
});

export const aveugleColoriageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coloriage/comparaisons/$id/juger",
  component: function AveugleColoriageRouteComponent() {
    const { id } = aveugleColoriageRoute.useParams();
    return <ColoriageAveuglePage id={id} />;
  },
});

/**
 * Le module Histoires : l'accueil des livres (#225), un livre et une double (#226). L'onglet
 * d'un livre passe dans l'adresse (`?onglet=brief`, `?onglet=personnages`) : « Voir le brief »
 * depuis une double y mene, un rechargement y retombe. Une autre valeur est ignoree, le
 * chemin de fer s'affiche. Le jugement arrive avec le ticket suivant, sous `/histoires/...`.
 */
export const histoiresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/histoires",
  component: HistoiresAccueilPage,
});

export const livreHistoiresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/histoires/livres/$id",
  // Le routeur fusionne ce que rend `validateSearch` avec l'adresse lue : une valeur refusee
  // doit donc etre effacee (`undefined`), pas seulement omise, sinon elle passe telle quelle.
  validateSearch: (
    search: Record<string, unknown>,
  ): { onglet?: "brief" | "personnages" | undefined } => ({
    onglet:
      search.onglet === "brief" || search.onglet === "personnages" ? search.onglet : undefined,
  }),
  component: function LivreHistoiresRouteComponent() {
    const { id } = livreHistoiresRoute.useParams();
    const { onglet } = livreHistoiresRoute.useSearch();
    return <HistoiresLivrePage id={id} onglet={onglet ?? "chemin"} />;
  },
});

export const doubleHistoiresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/histoires/doubles/$id",
  component: function DoubleHistoiresRouteComponent() {
    const { id } = doubleHistoiresRoute.useParams();
    return <HistoiresDoublePage id={id} />;
  },
});

/**
 * Le module Livres-jeux (#276) : l'accueil, un livre et une scene. L'onglet d'un livre passe
 * dans l'adresse (`?onglet=trame`) : un rechargement ou un lien copie y retombent ; une autre
 * valeur est effacee, les scenes s'affichent.
 */
const ONGLETS_DE_L_ADRESSE: readonly OngletJeu[] = [
  "scenes",
  "trame",
  "reglages",
  "livre",
  "publication",
];

export const livresJeuxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/livres-jeux",
  component: LivresJeuxAccueilPage,
});

export const livreJeuRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/livres-jeux/livres/$id",
  validateSearch: (search: Record<string, unknown>): { onglet?: OngletJeu | undefined } => ({
    onglet: ONGLETS_DE_L_ADRESSE.find((onglet) => onglet === search.onglet),
  }),
  component: function LivreJeuRouteComponent() {
    const { id } = livreJeuRoute.useParams();
    const { onglet } = livreJeuRoute.useSearch();
    return <LivresJeuxLivrePage id={id} onglet={onglet ?? "scenes"} />;
  },
});

export const sceneJeuRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/livres-jeux/scenes/$id",
  component: function SceneJeuRouteComponent() {
    const { id } = sceneJeuRoute.useParams();
    return <LivresJeuxScenePage id={id} />;
  },
});

export const appRoutes = [
  portefeuilleRoute,
  ideeRoute,
  gesteIdeeRoute,
  relanceRoute,
  gesteRoute,
  scoutRoute,
  silhouettesRoute,
  cadresRoute,
  bobinesRoute,
  coloriageRoute,
  cahierColoriageRoute,
  pageColoriageRoute,
  variantesColoriageRoute,
  comparaisonColoriageRoute,
  aveugleColoriageRoute,
  histoiresRoute,
  livreHistoiresRoute,
  doubleHistoiresRoute,
  livresJeuxRoute,
  livreJeuRoute,
  sceneJeuRoute,
];
